import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import API from '../api';
import { useAuth } from './AuthContext';

const WishlistContext = createContext();

export function WishlistProvider({ children }) {
  const { isLoggedIn, user } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastUserRef = useRef(null);

  // Genera o recupera session_id guest
  const getSessionId = () => {
    let sessionId = localStorage.getItem('wishlist_session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('wishlist_session_id', sessionId);
    }
    return sessionId;
  };

  // Carica la wishlist dal backend (utente loggato o guest)
  useEffect(() => {
    const fetchWishlist = async () => {
      setLoading(true);
      try {
        if (isLoggedIn) {
          const res = await API.get('/wishlist');
          setWishlist(res.data.wishlist.map(item => item.product_id));
          console.log('[WishlistContext] Wishlist utente caricata:', res.data.wishlist.length, 'prodotti');
        } else {
          const session_id = getSessionId();
          const res = await API.get('/wishlist/guest', { params: { session_id } });
          setWishlist(res.data.wishlist.map(item => item.product_id));
          console.log('[WishlistContext] Wishlist guest caricata:', res.data.wishlist.length, 'prodotti');
        }
      } catch (err) {
        console.error('[WishlistContext] Errore nel caricamento wishlist:', err);
        setWishlist([]);
      } finally {
        setLoading(false);
      }
    };

    // Controlla se l'utente è appena cambiato (login/logout)
    const userChanged = lastUserRef.current !== user?.id;
    lastUserRef.current = user?.id;

    fetchWishlist();
  }, [isLoggedIn, user?.id]); // Usa user.id invece di user per essere più specifico

  // Ricarica la wishlist quando l'utente si logga (per gestire la migrazione)
  useEffect(() => {
    if (isLoggedIn && user?.id && lastUserRef.current === user.id) {
      // Se l'utente è loggato e non è appena cambiato, potrebbe essere dopo una migrazione
      // Ricarica la wishlist dopo un breve delay per permettere alla migrazione di completarsi
      const timer = setTimeout(async () => {
        try {
          const res = await API.get('/wishlist');
          setWishlist(res.data.wishlist.map(item => item.product_id));
          console.log('[WishlistContext] Wishlist ricaricata dopo migrazione:', res.data.wishlist.length, 'prodotti');
        } catch (err) {
          console.error('[WishlistContext] Errore nel ricaricamento wishlist:', err);
        }
      }, 1000); // 1 secondo di delay

      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, user?.id]);

  // Aggiungi prodotto
  const addToWishlist = async (productId) => {
    if (!productId) return;
    setWishlist(prev => prev.includes(productId) ? prev : [...prev, productId]);
    try {
      if (isLoggedIn) {
        await API.post('/wishlist', { product_id: productId });
      } else {
        const session_id = getSessionId();
        await API.post('/wishlist/guest', { session_id, product_id: productId });
      }
    } catch (err) {}
  };

  // Rimuovi prodotto
  const removeFromWishlist = async (productId) => {
    setWishlist(prev => prev.filter(id => id !== productId));
    try {
      if (isLoggedIn) {
        await API.delete(`/wishlist/${productId}`);
      } else {
        const session_id = getSessionId();
        await API.delete(`/wishlist/guest/${productId}`, { params: { session_id } });
      }
    } catch (err) {}
  };

  return (
    <WishlistContext.Provider value={{ wishlist, addToWishlist, removeFromWishlist, loading }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  return useContext(WishlistContext);
} 